import type { ChangeEvent, HTMLAttributes, PropsWithChildren, ReactNode } from "react";
import { useEffect, useRef, useState } from "react";

type ImageFileTypes =
  | "image/avif"
  | "image/apng"
  | "image/gif"
  | "image/jpeg"
  | "image/png"
  | "image/svg+xml"
  | "image/webp"
  // Note: The following are not supported by all browsers
  | "image/bmp"
  | "image/tiff"
  | "image/x-icon";

type PDFFileTypes = "application/pdf";

//Add other file types when needed
export type FileTypes = ImageFileTypes | PDFFileTypes;

export const BASE_FILE_SIZE_LIMIT_BYTES = 4 * 1024 * 1024;

export type FileHandlingProps = {
  acceptedFileTypes: FileTypes[];
  onFileTypeError?: (file: File) => void;
  fileSizeLimitBytes?: number;
  onFileSizeLimitExceeded?: (file: File) => void;
}

export type DropzoneProps = HTMLAttributes<HTMLDivElement> &
  FileHandlingProps & {
    onDragStateChange?: (isDragActive: boolean) => void;
    onDrag?: () => void;
    onDragIn?: () => void;
    onDragOut?: () => void;
    onDrop?: () => void;
    onFilesDrop?: (files: File[]) => void;
  };

const convertAndCheckConstraints = ({
  files,
  fileSizeLimitBytes = BASE_FILE_SIZE_LIMIT_BYTES,
  acceptedFileTypes,
  onFileTypeError,
  onFileSizeLimitExceeded,
}: FileHandlingProps & {
  files: FileList;
}) => {
  const file = files.item(0) as File;
  if (!file) return [];

  if (!acceptedFileTypes.includes(file.type as ImageFileTypes)) {
    onFileTypeError?.(file);
    return [];
  }

  if (fileSizeLimitBytes && file.size > fileSizeLimitBytes) {
    onFileSizeLimitExceeded?.(file);
    return [];
  }

  return [file];
};

export const Dropzone = ({
  onDragStateChange,
  onFilesDrop,
  onDrag,
  onDragIn,
  onDragOut,
  onDrop,
  children,
  acceptedFileTypes,
  onFileTypeError,
  fileSizeLimitBytes,
  onFileSizeLimitExceeded,
  ...props
}: PropsWithChildren<DropzoneProps>) => {
  const [isDragActive, setIsDragActive] = useState(false);

  const dropzoneRef = useRef<null | HTMLDivElement>(null);

  const handleDragIn = (event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onDragIn?.();

    if (event.dataTransfer?.items && event.dataTransfer.items.length > 0) {
      setIsDragActive(true);
    }
  };

  const handleDragOut = (event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onDragOut?.();

    setIsDragActive(false);
  };

  const handleDrag = (event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onDrag?.();

    if (!isDragActive) {
      setIsDragActive(true);
    }
  };

  const handleDrop = (event: DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    onDrop?.();

    setIsDragActive(false);

    if (event.dataTransfer?.files && event.dataTransfer.files.length > 0) {
      const files = convertAndCheckConstraints({
        files: event.dataTransfer.files,
        fileSizeLimitBytes,
        acceptedFileTypes,
        onFileTypeError,
        onFileSizeLimitExceeded,
      });

      onFilesDrop?.(files);
      event.dataTransfer.clearData();
    }
  };

  useEffect(() => {
    onDragStateChange?.(isDragActive);
  }, [isDragActive]);

  useEffect(() => {
    const tempZoneRef = dropzoneRef?.current;
    if (tempZoneRef) {
      tempZoneRef.addEventListener("dragenter", handleDragIn);
      tempZoneRef.addEventListener("dragleave", handleDragOut);
      tempZoneRef.addEventListener("dragover", handleDrag);
      tempZoneRef.addEventListener("drop", handleDrop);
    }

    return () => {
      tempZoneRef?.removeEventListener("dragenter", handleDragIn);
      tempZoneRef?.removeEventListener("dragleave", handleDragOut);
      tempZoneRef?.removeEventListener("dragover", handleDrag);
      tempZoneRef?.removeEventListener("drop", handleDrop);
    };
  }, []);

  return (
    <div ref={dropzoneRef} {...props}>
      {children}
    </div>
  );
};

Dropzone.displayName = "Dropzone";

type FileInputProps = FileHandlingProps & {
  children: ReactNode;
  onAddFiles?: (files: File[]) => void;
  fileInputId: string;
  disabled?: boolean;
  resetInput?: boolean;
};

export const FileInput = ({
  acceptedFileTypes,
  onFileTypeError,
  fileSizeLimitBytes,
  onFileSizeLimitExceeded,
  onAddFiles,
  children,
  fileInputId,
  disabled,
  resetInput = false,
}: FileInputProps) => {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (event.target.files) {
      const files = convertAndCheckConstraints({
        files: event.target.files,
        fileSizeLimitBytes,
        acceptedFileTypes,
        onFileTypeError,
        onFileSizeLimitExceeded,
      });

      onAddFiles?.(files);
      if (resetInput) {
        event.target.value = "";
      }
    }
  };

  return (
    <>
      <label htmlFor={fileInputId}>{children}</label>
      <input disabled={disabled} type="file" id={fileInputId} className="hidden" onChange={handleChange}></input>
    </>
  );
};
